// Phase 10.8 — Signage app renderer.
//
// Playlist items are either a template OR an app (enforced by the
// playlist_items_template_or_app CHECK). player.jsx branches on
// `item.app` and hands the app here as { kind, config }.
//
// The kinds and their config shapes are defined in
// backend/lib/apps/catalog.js — that file is the contract. When you add
// an app there, add a case in APPS below.
//
// Two rules this file follows, because it runs unattended on a TV that
// nobody is watching over:
//
//   1. NEVER render nothing. Every failure path draws a readable panel
//      instead of a black screen, so a bad URL looks like a mistake to
//      fix rather than a dead display.
//   2. NEVER throw. A render error here would unmount the whole player
//      and take template playback down with it. An error boundary wraps
//      every app for that reason.
//
// The player owns timing: it advances to the next item after the item's
// durationSec. Apps that cycle internally (slideshows) just loop until
// they are unmounted.

import { Component, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet } from './api.js';

// Mirrors api.js. Media is referenced by <img>/<video> src rather than
// fetch(), so it needs the absolute origin baked in the same way.
const API_BASE = (import.meta.env?.VITE_API_BASE || '').replace(/\/$/, '');

// Uploads are static-served from /uploads/<id> (see backend/server.js).
function assetUrl(pathOrId) {
  if (!pathOrId) return '';
  const s = String(pathOrId);
  if (/^https?:\/\//i.test(s)) return s;
  const path = s.startsWith('/uploads/') ? s : `/uploads/${s.replace(/^\/+/, '')}`;
  return API_BASE ? `${API_BASE}${path}` : path;
}

/* ─── shared chrome ──────────────────────────────────────────────────── */

const root = {
  width: '100%',
  height: '100%',
  background: '#000',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const frame = {
  width: '100%',
  height: '100%',
  border: 0,
  display: 'block',
  background: '#000',
};

// Third-party embeds need their own scripts and origin. They are all
// cross-origin, so allow-same-origin grants them *their* origin, not ours.
const EMBED_SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-presentation';

function Notice({ title, detail }) {
  return (
    <div
      style={{
        ...root,
        flexDirection: 'column',
        gap: 10,
        color: '#9ca3af',
        textAlign: 'center',
        padding: '6%',
        fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
        background: '#08061a',
      }}
    >
      <div style={{ fontSize: 'clamp(16px, 2.4cqw, 30px)', color: '#e5e7eb', fontWeight: 700 }}>
        {title}
      </div>
      {detail ? (
        <div style={{ fontSize: 'clamp(12px, 1.4cqw, 18px)', opacity: 0.75, maxWidth: '70%' }}>
          {detail}
        </div>
      ) : null}
    </div>
  );
}

/* ─── embed / website ────────────────────────────────────────────────── */

function EmbedApp({ config }) {
  if (!config?.url) return <Notice title="Embed has no URL" />;
  return (
    <iframe
      title="signage-embed"
      src={config.url}
      style={frame}
      sandbox={EMBED_SANDBOX}
      referrerPolicy="no-referrer"
      loading="eager"
    />
  );
}

function WebsiteApp({ config }) {
  const ref = useRef(null);
  const zoom = Number(config?.zoom) || 100;
  const autoscroll = !!config?.autoscroll;

  // Long pages: creep the iframe upward so the whole page is seen during
  // the item's slot, then snap back and repeat. We cannot read the remote
  // document's height (cross-origin), so we scroll the wrapper we own and
  // give the iframe extra height to reveal.
  useEffect(() => {
    if (!autoscroll) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    let y = 0;
    const step = () => {
      y += 0.35;                                  // ~21px/sec — readable pace
      const max = el.scrollHeight - el.clientHeight;
      if (max > 0 && y >= max) y = 0;
      el.scrollTop = y;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [autoscroll]);

  if (!config?.url) return <Notice title="Website has no URL" />;

  const scale = zoom / 100;
  return (
    <div ref={ref} style={{ ...root, overflow: autoscroll ? 'hidden' : 'hidden' }}>
      <iframe
        title="signage-website"
        src={config.url}
        sandbox={EMBED_SANDBOX}
        referrerPolicy="no-referrer"
        style={{
          ...frame,
          // Counter-scale the box so a zoomed page still fills the screen.
          width: `${100 / scale}%`,
          height: autoscroll ? `${(100 / scale) * 2.5}%` : `${100 / scale}%`,
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
        }}
      />
    </div>
  );
}

/* ─── youtube ────────────────────────────────────────────────────────── */

function YouTubeApp({ config }) {
  const id = config?.videoId;
  if (!id) return <Notice title="YouTube item has no video" />;

  // Autoplay on a TV only works muted — every browser blocks audible
  // autoplay. `loop` needs `playlist=<id>` set to the same id.
  const params = new URLSearchParams({
    autoplay: '1',
    mute: config?.mute === false ? '0' : '1',
    controls: '0',
    modestbranding: '1',
    rel: '0',
    playsinline: '1',
    loop: '1',
    playlist: id,
    start: String(config?.start || 0),
  });

  return (
    <iframe
      title="signage-youtube"
      src={`https://www.youtube-nocookie.com/embed/${id}?${params}`}
      style={frame}
      allow="autoplay; encrypted-media; picture-in-picture"
      sandbox={EMBED_SANDBOX}
    />
  );
}

/* ─── video ──────────────────────────────────────────────────────────── */

function VideoApp({ config }) {
  const src = config?.url || assetUrl(config?.uploadId);
  const ref = useRef(null);

  // Some TV browsers ignore the autoplay attribute but honour play() once
  // the element is muted and attached.
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const go = () => v.play().catch(() => {});
    if (v.readyState >= 2) go();
    else v.addEventListener('loadeddata', go, { once: true });
  }, [src]);

  if (!src) return <Notice title="Video item has no source" />;

  const fit = config?.fit === 'stretch' ? 'fill'
    : config?.fit === 'contain' ? 'contain'
    : 'cover';

  return (
    <div style={root}>
      <video
        ref={ref}
        src={src}
        autoPlay
        muted={config?.muted !== false}
        loop={config?.loop !== false}
        playsInline
        style={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }}
      />
    </div>
  );
}

/* ─── slideshows ─────────────────────────────────────────────────────── */

// Shared by image_slideshow and gdrive_slideshow. Cross-fades so the
// change reads as deliberate rather than a flicker.
function Slideshow({ urls, perImageSec = 5, fit = 'cover', emptyTitle = 'Slideshow is empty' }) {
  const [i, setI] = useState(0);
  const count = urls.length;

  useEffect(() => {
    if (count < 2) return undefined;
    const ms = Math.max(2, Number(perImageSec) || 5) * 1000;
    const t = setInterval(() => setI((n) => (n + 1) % count), ms);
    return () => clearInterval(t);
  }, [count, perImageSec]);

  if (!count) return <Notice title={emptyTitle} />;

  return (
    <div style={root}>
      {urls.map((u, n) => (
        <img
          key={`${u}-${n}`}
          src={u}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: fit === 'contain' ? 'contain' : 'cover',
            opacity: n === i ? 1 : 0,
            transition: 'opacity 700ms ease',
          }}
        />
      ))}
    </div>
  );
}

function ImageSlideshowApp({ config }) {
  const urls = useMemo(
    () => (Array.isArray(config?.images) ? config.images : [])
      .map((img) => img?.url || assetUrl(img?.uploadId))
      .filter(Boolean),
    [config],
  );
  return (
    <Slideshow
      urls={urls}
      perImageSec={config?.perImageSec}
      fit={config?.fit}
      emptyTitle="Slideshow has no images"
    />
  );
}

function GDriveSlideshowApp({ config }) {
  const folderId = config?.folderId;
  const [state, setState] = useState({ status: 'loading', urls: [], error: null });

  useEffect(() => {
    if (!folderId) { setState({ status: 'error', urls: [], error: 'No folder linked' }); return undefined; }
    let alive = true;
    apiGet(`/apps/gdrive?folderId=${encodeURIComponent(folderId)}&n=40`)
      .then((r) => {
        if (!alive) return;
        const urls = (r?.images || []).map((im) => im.thumbUrl || im.url).filter(Boolean);
        setState({ status: 'ready', urls, error: null });
      })
      .catch((e) => {
        if (!alive) return;
        // 503 means GOOGLE_API_KEY isn't set on the server — say so plainly
        // rather than leaving someone staring at an empty screen.
        setState({ status: 'error', urls: [], error: e?.message || 'Could not reach Drive' });
      });
    return () => { alive = false; };
  }, [folderId]);

  if (state.status === 'loading') return <Notice title="Loading Drive folder…" />;
  if (state.status === 'error') return <Notice title="Drive folder unavailable" detail={state.error} />;
  return (
    <Slideshow
      urls={state.urls}
      perImageSec={config?.perImageSec || 6}
      fit={config?.fit}
      emptyTitle="No images in that Drive folder"
    />
  );
}

/* ─── rss ────────────────────────────────────────────────────────────── */

function RssApp({ config }) {
  const feedUrl = config?.feedUrl;
  const maxItems = Number(config?.maxItems) || 8;
  const style = config?.style || 'ticker';
  const refreshMin = Number(config?.refreshMin) || 30;
  const [state, setState] = useState({ status: 'loading', items: [], error: null });

  useEffect(() => {
    if (!feedUrl) { setState({ status: 'error', items: [], error: 'No feed URL' }); return undefined; }
    let alive = true;
    const load = () => {
      apiGet(`/apps/rss?url=${encodeURIComponent(feedUrl)}&n=${maxItems}`)
        .then((r) => alive && setState({ status: 'ready', items: r?.items || [], error: null }))
        .catch((e) => alive && setState((s) => (
          // Keep showing the last good headlines if a refresh fails — stale
          // news beats an error panel on a wall display.
          s.items.length
            ? s
            : { status: 'error', items: [], error: e?.message || 'Feed unavailable' }
        )));
    };
    load();
    const t = setInterval(load, Math.max(5, refreshMin) * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [feedUrl, maxItems, refreshMin]);

  if (state.status === 'loading') return <Notice title="Loading feed…" />;
  if (state.status === 'error') return <Notice title="Feed unavailable" detail={state.error} />;
  if (!state.items.length) return <Notice title="Feed has no headlines" />;

  const shell = {
    ...root,
    background: 'linear-gradient(160deg,#0b1220 0%,#111a2e 100%)',
    color: '#f4f6fb',
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '5%',
  };

  if (style === 'ticker') {
    // Duplicated track so the loop has no visible seam.
    const track = state.items.map((it) => it.title);
    return (
      <div style={{ ...shell, justifyContent: 'center', padding: 0 }}>
        <style>{`@keyframes rss-scroll{from{transform:translate3d(0,0,0)}to{transform:translate3d(-50%,0,0)}}`}</style>
        <div style={{ width: '100%', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          <div
            style={{
              display: 'inline-block',
              animation: `rss-scroll ${Math.max(20, track.length * 7)}s linear infinite`,
              fontSize: 'clamp(20px, 3.4cqw, 54px)',
              fontWeight: 700,
            }}
          >
            {[0, 1].map((dup) => (
              <span key={dup}>
                {track.map((t, n) => (
                  <span key={`${dup}-${n}`} style={{ paddingRight: '4cqw' }}>
                    <span style={{ color: '#ff8a63', paddingRight: '1.2cqw' }}>●</span>
                    {t}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (style === 'card') {
    const top = state.items[0];
    return (
      <div style={{ ...shell, alignItems: 'flex-start', gap: '2cqh' }}>
        <div style={{ fontSize: 'clamp(11px,1.2cqw,16px)', letterSpacing: '0.14em', color: '#ff8a63', fontWeight: 800 }}>
          LATEST
        </div>
        <div style={{ fontSize: 'clamp(24px,4.2cqw,68px)', fontWeight: 800, lineHeight: 1.12 }}>
          {top?.title}
        </div>
        {state.items[1] ? (
          <div style={{ fontSize: 'clamp(14px,1.7cqw,26px)', opacity: 0.62 }}>
            Next: {state.items[1].title}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ ...shell, alignItems: 'flex-start', gap: '1.6cqh' }}>
      {state.items.map((it, n) => (
        <div key={n} style={{ display: 'flex', gap: '1.6cqw', alignItems: 'baseline' }}>
          <span style={{ color: '#ff8a63', fontWeight: 800, fontSize: 'clamp(12px,1.5cqw,22px)' }}>
            {String(n + 1).padStart(2, '0')}
          </span>
          <span style={{ fontSize: 'clamp(15px,2.1cqw,34px)', fontWeight: 600, lineHeight: 1.25 }}>
            {it.title}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── social / design embeds ─────────────────────────────────────────── */

function InstagramApp({ config }) {
  if (!config?.shortcode) return <Notice title="Instagram item has no post" />;
  // The public /embed/ endpoint renders one post without OAuth. It is
  // portrait-ish, so centre it on the black field rather than stretching.
  return (
    <div style={root}>
      <iframe
        title="signage-instagram"
        src={`https://www.instagram.com/p/${config.shortcode}/embed/captioned/`}
        sandbox={EMBED_SANDBOX}
        scrolling="no"
        style={{ ...frame, width: 'min(100%, 62vh)', height: '100%', background: '#fff' }}
      />
    </div>
  );
}

function FacebookPageApp({ config }) {
  if (!config?.pageUrl) return <Notice title="Facebook item has no page" />;
  const params = new URLSearchParams({
    href: config.pageUrl,
    tabs: config?.tabs || 'timeline',
    width: '500',
    height: '780',
    small_header: 'true',
    adapt_container_width: 'true',
    hide_cover: 'false',
    show_facepile: 'false',
  });
  return (
    <div style={root}>
      <iframe
        title="signage-facebook"
        src={`https://www.facebook.com/plugins/page.php?${params}`}
        sandbox={EMBED_SANDBOX}
        scrolling="no"
        style={{ ...frame, width: 'min(100%, 56vh)', height: '100%', background: '#fff' }}
      />
    </div>
  );
}

function CanvaApp({ config }) {
  if (!config?.designId) return <Notice title="Canva item has no design" />;
  return (
    <iframe
      title="signage-canva"
      src={`https://www.canva.com/design/${config.designId}/view?embed`}
      sandbox={EMBED_SANDBOX}
      allow="fullscreen"
      style={frame}
    />
  );
}

/* ─── registry ───────────────────────────────────────────────────────── */
// Keys match `kind` in backend/lib/apps/catalog.js.

const APPS = {
  embed: EmbedApp,
  website: WebsiteApp,
  youtube: YouTubeApp,
  video: VideoApp,
  image_slideshow: ImageSlideshowApp,
  gdrive_slideshow: GDriveSlideshowApp,
  rss: RssApp,
  instagram: InstagramApp,
  facebook_page: FacebookPageApp,
  canva: CanvaApp,
};

/* ─── error boundary ─────────────────────────────────────────────────── */
// player.jsx branches app vs template specifically so "a bug in app
// rendering can never break template playback". This keeps that promise:
// a throw inside any app is caught here and drawn as a panel, and the
// player carries on to the next item on schedule.

class AppBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, message: '' };
  }

  static getDerivedStateFromError(err) {
    return { failed: true, message: err?.message || 'Render error' };
  }

  componentDidCatch(err) {
    console.warn('[AppRenderer] app crashed:', err?.message || err);
  }

  render() {
    if (this.state.failed) {
      return <Notice title="This app could not be displayed" detail={this.state.message} />;
    }
    return this.props.children;
  }
}

/* ─── entry point ────────────────────────────────────────────────────── */

export default function AppRenderer({ app }) {
  if (!app?.kind) return <Notice title="Empty playlist item" />;

  const Impl = APPS[app.kind];
  if (!Impl) {
    // A newer manager build can schedule a kind this player does not know
    // yet. Name it, so the fix is obvious: update the player.
    return <Notice title="Unsupported app" detail={`This screen does not support "${app.kind}" yet.`} />;
  }

  return (
    <AppBoundary key={app.kind}>
      <Impl config={app.config || {}} />
    </AppBoundary>
  );
}
